// File: frontend/src/pages/AIAgents/components/AgentShowcase.js

import React from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import LandingButton from '../../../components/Button/LandingButton';
import styles from './AgentShowcase.module.css';

const AgentShowcase = ({ agent, categoryId, onOpenVideo }) => {
  const { t } = useLanguage();
  const Icon = LucideIcons[agent.icon];
  const VideoIcon = LucideIcons['Play'];
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div 
        key={agent.id}
        className={styles.container}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <div className={styles.infoSection}>
          <div className={styles.headerSection}>
            <motion.div 
              className={styles.iconContainer}
              style={{ background: agent.gradient }}
              variants={itemVariants}
            >
              <Icon className={styles.icon} aria-hidden="true" />
            </motion.div>
            
            {agent.video && (
              <motion.button
                className={styles.videoButton}
                onClick={() => onOpenVideo(agent.video)}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={`Watch ${agent.name} demo video`}
              >
                <VideoIcon className={styles.videoIcon} />
                {/* <span>{t('ai_agents.watch_demo')}</span> */}
              </motion.button>
            )}
          </div>
          
          <motion.h2 
            className={styles.agentName}
            variants={itemVariants}
          >
            {agent.name}
          </motion.h2>
          
          <motion.p 
            className={styles.agentDescription}
            variants={itemVariants}
          >
            {agent.description}
          </motion.p>
        </div>
        
        <div className={styles.capabilitiesSection}>
          <motion.h3 
            className={styles.capabilitiesTitle}
            variants={itemVariants}
          >
            {t('ai_agents.capabilities_title')}
          </motion.h3>
          
          <motion.ul 
            className={styles.capabilitiesList}
            variants={containerVariants}
          >
            {agent.capabilities.map((capability, index) => (
              <motion.li 
                key={index}
                className={styles.capabilityItem}
                variants={itemVariants}
              >
                <div className={styles.bullet} />
                <span>{capability}</span>
              </motion.li>
            ))}
          </motion.ul>
          
          <motion.div 
            className={styles.ctaContainer}
            variants={itemVariants}
          >
            <LandingButton 
              href={`/sign-in`}
              className={styles.agentCta}
              aria-label={`${t('access')} ${agent.name}`}
            >
              {`${t('access')} ${agent.name}`}
            </LandingButton>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

AgentShowcase.propTypes = {
  agent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    capabilities: PropTypes.arrayOf(PropTypes.string).isRequired,
    icon: PropTypes.string.isRequired,
    gradient: PropTypes.string.isRequired,
    video: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
      poster: PropTypes.string.isRequired
    })
  }).isRequired,
  categoryId: PropTypes.string.isRequired,
  onOpenVideo: PropTypes.func.isRequired
};

export default AgentShowcase;